import { TransactionDetailOptionFormType } from '@/features/transaction_items/Detail/type';
import { FormInput } from '@moneyforward/ap-frontend-components';
import classNames from 'classnames';
import { FC, memo, useCallback, useEffect, useMemo, useRef } from 'react';
import {
  Control,
  RegisterOptions,
  UseFormClearErrors,
  useController,
  useFormState,
} from 'react-hook-form';
import styles from './DebounceFormInput.module.scss';

const cx = classNames.bind(styles);

export type Props = {
  control: Control<TransactionDetailOptionFormType>;
  name: keyof TransactionDetailOptionFormType;
  inputId: string;
  testId: string;
  rules?: RegisterOptions<TransactionDetailOptionFormType>;
  transform?: (value: string) => string;
  debounceMs?: number;
  descriptionMessage?: string;
  clearErrors: UseFormClearErrors<TransactionDetailOptionFormType>;
};

export const DebounceFormInput: FC<Props> = memo(
  ({
    control,
    name,
    inputId,
    testId,
    rules,
    transform,
    debounceMs = 2000,
    descriptionMessage,
    clearErrors,
  }) => {
    const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);

    const { errors } = useFormState({ control });
    const hasError = !!errors[name]?.message;

    const validate = useMemo(() => {
      if (!rules) return () => undefined;

      return (value: string | number | boolean): string | undefined => {
        const stringValue = String(value || '');

        if (rules.required) {
          if (!stringValue) {
            return typeof rules.required === 'string'
              ? rules.required
              : 'This field is required';
          }
        }

        if (rules.pattern) {
          let pattern: RegExp;
          let message = 'Invalid format';

          if (rules.pattern instanceof RegExp) {
            pattern = rules.pattern;
          } else if (
            typeof rules.pattern === 'object' &&
            'value' in rules.pattern
          ) {
            pattern = rules.pattern.value;
            message = rules.pattern.message || message;
          } else {
            return undefined; // Invalid pattern format
          }

          if (!pattern.test(stringValue)) {
            return message;
          }
        }

        if (rules.maxLength) {
          const maxLength =
            typeof rules.maxLength === 'object'
              ? rules.maxLength.value
              : rules.maxLength;
          if (stringValue.length > maxLength) {
            return typeof rules.maxLength === 'object' &&
              rules.maxLength.message
              ? rules.maxLength.message
              : `Maximum length is ${maxLength}`;
          }
        }

        if (rules.minLength) {
          const minLength =
            typeof rules.minLength === 'object'
              ? rules.minLength.value
              : rules.minLength;
          if (stringValue.length < minLength) {
            return typeof rules.minLength === 'object' &&
              rules.minLength.message
              ? rules.minLength.message
              : `Minimum length is ${minLength}`;
          }
        }

        return undefined;
      };
    }, [rules]);

    const {
      field: { value, onChange: fieldOnChange },
    } = useController({
      name,
      control,
      defaultValue: '',
    });

    const latestValueRef = useRef<string>('');

    const applyValidation = useCallback(
      (v: string, immediate?: boolean) => {
        const message = validate(v);
        const setError = () => {
          control.setError(name, { type: 'manual', message });
        };

        if (!message) {
          clearErrors(name);
          return;
        }

        if (immediate === true) {
          setError();
        } else {
          debounceTimerRef.current = setTimeout(setError, debounceMs);
        }
      },
      [clearErrors, control, name, validate, debounceMs]
    );

    const handleInputChange = useCallback(
      (newValue: string) => {
        latestValueRef.current = newValue;
        fieldOnChange(newValue);

        if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);

        const scheduledValue = newValue;

        if (latestValueRef.current !== scheduledValue) return;
        applyValidation(scheduledValue);
      },
      [fieldOnChange, applyValidation]
    );

    const handleBlur = useCallback(() => {
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);

      const currentValue = String(value ?? '');
      let processedValue = currentValue;

      if (transform) {
        processedValue = transform(currentValue);
        if (processedValue !== currentValue) {
          latestValueRef.current = processedValue;
          fieldOnChange(processedValue);
        }
      }

      applyValidation(processedValue, true);
    }, [value, transform, fieldOnChange, applyValidation]);

    useEffect(() => {
      return () => {
        if (debounceTimerRef.current) {
          clearTimeout(debounceTimerRef.current);
        }
      };
    }, []);

    return (
      <div>
        <FormInput
          control={control}
          name={name}
          inputId={inputId}
          rules={rules}
          onChange={handleInputChange}
          onBlur={handleBlur}
          testId={testId}
        />
        {!hasError && descriptionMessage && (
          <div className={cx(styles['description-message'])}>
            {descriptionMessage}
          </div>
        )}
      </div>
    );
  }
);

DebounceFormInput.displayName = 'DebounceFormInput';
